/**
 * The wishes on the Lab board beyond the arranger's choices: the key the score is moved into
 * first, how the players play and whether the page is made easier (`ArrangeWishes` in
 * `@app/shared-types`).
 *
 * The key holds whatever arranging starts from. How the players play and the ease hold from
 * the score and from the note model; starting from the tune every part is played as written,
 * so both are shut and sent as nothing. A playing is asked of every player or of the players
 * of one instrument, or of those an answer in words named. An easier page has no double stops:
 * asked for beside them, the run is refused rather than either dropped.
 */

import {
  EASES,
  KEY_FURTHEST,
  PLAYINGS,
  TONICS,
  type ArrangeKey,
  type ArrangeStart,
  type ArrangeWishes,
  type Ease,
  type Playing,
  type Seat,
} from "@app/shared-types";
import ArrangeMenu, { type Named } from "./ArrangeMenu";
import { seatLabel } from "./ArrangeRoster";
import { LABEL, SELECT } from "./Chrome";

/** The wishes as the board holds them. */
export interface Wishes {
  readonly playing: Playing;
  /** The instruments `playing` is asked of; every player where empty. */
  readonly playingWho: readonly string[];
  readonly ease: Ease;
  /** The key asked for, or `null` for the score's own. */
  readonly key: ArrangeKey | null;
}

export const NO_WISHES: Wishes = { playing: "written", playingWho: [], ease: "written", key: null };

/** The wishes a run starting at `start` sends: each left out where nothing is asked. */
export function wishesSent(
  { playing, playingWho, ease, key }: Wishes,
  start: ArrangeStart,
): ArrangeWishes {
  const played = start !== "tune";
  return {
    ...(key === null ? {} : { key }),
    ...(played && playing !== "written" ? { playing, playingWho: [...playingWho] } : {}),
    ...(played && ease !== "written" ? { ease } : {}),
  };
}

export const PLAYING_NAMES: Named<Playing> = {
  written: { label: "As written", title: "Every player plays as the score has it." },
  pizzicato: {
    label: "Pizzicato",
    title: "Bowed strings pluck every note, each seated in its plucked compass.",
  },
  staccato: { label: "Staccato", title: "A dot on every note of a quarter or shorter." },
  legato: {
    label: "Legato",
    title:
      "Each note slurred to the next within a bar, for every player but a plucked or struck one.",
  },
  muted: { label: "Muted", title: "Bowed strings and brass play with mutes." },
};

export const EASE_NAMES: Named<Ease> = {
  written: { label: "As written", title: "Every player is seated as the arranger seats them." },
  easier: {
    label: "Easier",
    title:
      "No player of one line over the top of its best register, a hand an octave and a note fewer than its fingers, no double stops. No rhythm made simpler and no note of the tune left out.",
  },
};

/** A key as the key menu holds it. */
function keyValue(key: ArrangeKey | null): string {
  if (key === null) return "";
  if ("by" in key) return `by:${key.by}`;
  return `to:${key.tonic}:${key.minor === null ? "" : key.minor ? "minor" : "major"}`;
}

function keyFrom(value: string): ArrangeKey | null {
  const [kind, first, mode] = value.split(":");
  if (kind === "by") return { by: Number(first) };
  const tonic = TONICS.find((one) => one === first);
  if (kind !== "to" || tonic === undefined) return null;
  return { tonic, minor: mode === "" ? null : mode === "minor" };
}

/** Every move the menu offers, the furthest down first, and none of nothing. */
const MOVES = [...Array(2 * KEY_FURTHEST).keys()].map((at) =>
  at < KEY_FURTHEST ? at - KEY_FURTHEST : at - KEY_FURTHEST + 1,
);

/** The key in a few words, as the folded strip and the menu say it. */
export function keySaid(key: ArrangeKey): string {
  if ("by" in key) {
    return `${Math.abs(key.by)} semitone${Math.abs(key.by) === 1 ? "" : "s"} ${key.by > 0 ? "up" : "down"}`;
  }
  const mode = key.minor === null ? "" : key.minor ? " minor" : " major";
  return `in ${key.tonic.replace("#", "♯").replace(/(?<=.)b/, "♭")}${mode}`;
}

/** What the folded strip says of the wishes, each after a dot; empty when none is asked. */
export function wishesSaid(wishes: Wishes, start: ArrangeStart): string {
  const sent = wishesSent(wishes, start);
  return [
    sent.key === undefined ? "" : ` · ${keySaid(sent.key)}`,
    sent.playing === undefined ? "" : ` · ${PLAYING_NAMES[sent.playing].label.toLowerCase()}`,
    sent.ease === undefined ? "" : " · easier",
  ].join("");
}

export default function ArrangeWishes({
  wishes,
  start,
  roster,
  seats,
  onWishes,
}: {
  readonly wishes: Wishes;
  readonly start: ArrangeStart;
  readonly roster: readonly string[];
  readonly seats: readonly Seat[];
  onWishes(wishes: Wishes): void;
}): JSX.Element {
  const shut =
    start === "tune" ? "Arranged anew from the tune, every part is played as written." : undefined;
  const instruments = [...new Set(roster)];
  const who = wishes.playingWho.join(",");
  const whoChoices = [
    "",
    ...instruments,
    ...(who !== "" && !instruments.includes(who) ? [who] : []),
  ];
  return (
    <>
      <span className={LABEL}>Key</span>
      <select
        value={keyValue(wishes.key)}
        title="The key the score is moved into before a note of it is arranged."
        onChange={(event) => onWishes({ ...wishes, key: keyFrom(event.target.value) })}
        className={SELECT}
      >
        <option value="">As written</option>
        {MOVES.map((by) => (
          <option key={by} value={`by:${by}`}>
            {keySaid({ by })}
          </option>
        ))}
        {TONICS.flatMap((tonic) =>
          ([null, false, true] as const).map((minor) => (
            <option key={`${tonic}${String(minor)}`} value={keyValue({ tonic, minor })}>
              {keySaid({ tonic, minor })}
            </option>
          )),
        )}
      </select>
      <ArrangeMenu
        label="Playing"
        value={wishes.playing}
        values={PLAYINGS}
        names={PLAYING_NAMES}
        shut={shut}
        onPick={(playing) =>
          onWishes({
            ...wishes,
            playing,
            playingWho: playing === "written" ? [] : wishes.playingWho,
          })
        }
      />
      <select
        value={who}
        disabled={shut !== undefined || wishes.playing === "written"}
        title="Who plays so: every player, or the players of one instrument."
        onChange={(event) =>
          onWishes({
            ...wishes,
            playingWho: event.target.value === "" ? [] : event.target.value.split(","),
          })
        }
        className={`${SELECT} disabled:opacity-40`}
      >
        {whoChoices.map((one) => (
          <option key={one} value={one}>
            {one === ""
              ? "Every player"
              : one
                  .split(",")
                  .map((seat) => seatLabel(seat, seats))
                  .join(", ")}
          </option>
        ))}
      </select>
      <ArrangeMenu
        label="Ease"
        value={wishes.ease}
        values={EASES}
        names={EASE_NAMES}
        shut={shut}
        onPick={(ease) => onWishes({ ...wishes, ease })}
      />
    </>
  );
}
